import { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { MoreHorizontal } from "lucide-react";
import { DeleteBtn } from "@/components/Delete";
import { TypeTableProps } from "@/App/Plans/Table";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { TableDataProps } from "@/pages/Reports";
import { deleteReportData } from "@/Api/reports";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";

type SortOrder = "asc" | "desc";

const getStatusColor = (statusId: number) => {
  switch (statusId) {
    case 1:
      return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400";
    case 2:
      return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
    case 3:
      return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
  }
};

export const TableData = ({
  dataApi,
  pageActions,
  updateFront,
}: TypeTableProps<TableDataProps>) => {
  const [sortField, setSortField] = useState<keyof TableDataProps | "">("");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const { hasPermission } = usePermissions();

  const handleSort = (field: keyof TableDataProps) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const sortedData = useMemo(() => {
    if (!sortField) return dataApi?.data || [];
    const sorted = [...(dataApi?.data || [])].sort((a, b) => {
      let valA: any = a[sortField as keyof TableDataProps];
      let valB: any = b[sortField as keyof TableDataProps];

      switch (sortField) {
        case "unity_id":
          valA = a.unity?.name || "";
          valB = b.unity?.name || "";
          break;
        case "report_status_id":
          valA = a.report_status?.name || "";
          valB = b.report_status?.name || "";
          break;
        case "report_type_id":
          valA = a.report_type?.name || "";
          valB = b.report_type?.name || "";
          break;
        case "generated_by_user_id":
          valA = a.generated_by_user?.name || "";
          valB = b.generated_by_user?.name || "";
          break;
        default:
          break;
      }

      const strA =
        typeof valA === "number" ? valA.toString() : (valA || "").toString();
      const strB =
        typeof valB === "number" ? valB.toString() : (valB || "").toString();

      if (strA < strB) return sortOrder === "asc" ? -1 : 1;
      if (strA > strB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [dataApi?.data, sortField, sortOrder]);

  const renderSortIndicator = (field: keyof TableDataProps) => {
    if (sortField !== field) return null;
    return sortOrder === "asc" ? " ▲" : " ▼";
  };

  const handleDownload = (filePath: string, fileName: string) => {
    if (!filePath) return;
    if (/^https?:\/\//.test(filePath)) {
      window.open(filePath, "_blank");
      return;
    }
    const url = `${process.env.NEXT_PUBLIC_API_URL || ""}/${filePath}`;
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || "arquivo";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead onClick={() => handleSort("name")}>
              Nome{renderSortIndicator("name")}
            </TableHead>
            <TableHead onClick={() => handleSort("report_type_id")}>
              Tipo de Relatório{renderSortIndicator("report_type_id")}
            </TableHead>
            <TableHead onClick={() => handleSort("file_name")}>
              Arquivo{renderSortIndicator("file_name")}
            </TableHead>
            {/* <TableHead onClick={() => handleSort("unity_id")}>
              Unidade{renderSortIndicator("unity_id")}
            </TableHead> */}
            <TableHead onClick={() => handleSort("report_status_id")}>
              Status{renderSortIndicator("report_status_id")}
            </TableHead>
            <TableHead onClick={() => handleSort("generated_by_user_id")}>
              Gerado por{renderSortIndicator("generated_by_user_id")}
            </TableHead>
            <TableHead>Gerado em</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedData.map((item) => (
            <TableRow key={item?.id}>
              <TableCell className="truncate max-w-80">{item?.name}</TableCell>
              <TableCell>{item?.report_type?.name}</TableCell>
              <TableCell
                className="truncate max-w-80"
                onClick={() => handleDownload(item.file_path, item.file_name)}
              >
                {item?.file_name}
              </TableCell>
              <TableCell>
                <span
                  className={cn(
                    "px-2 py-1 rounded-full text-xs font-medium",
                    getStatusColor(item?.report_status_id)
                  )}
                >
                  {item?.report_status?.name}
                </span>
              </TableCell>
              <TableCell>{item?.generated_by_user?.name}</TableCell>
              <TableCell>
                {item?.created_at
                  ? new Date(item.created_at).toLocaleString("pt-BR", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                      hour12: false,
                      timeZone: "America/Sao_Paulo",
                    })
                  : ""}
              </TableCell>
              <TableCell className="flex justify-center">
                <div className="flex gap-3 w-full justify-center">
                  <If condition={hasPermission("download-report")}>
                    {item?.file_path && (
                      <button
                        className="ml-2 text-blue-600 hover:text-blue-800"
                        onClick={() =>
                          handleDownload(item.file_path, item.file_name)
                        }
                        title="Baixar arquivo"
                        type="button"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                          />
                        </svg>
                      </button>
                    )}
                  </If>

                  {/* <UpdateBtn
                    uuid={item?.uuid}
                    updateFront={(response) => {
                      updateFront((prev) => {
                        prev?.data?.splice(index, 1, response);
                        return {
                          ...prev,
                          data: [...prev.data],
                        };
                      });
                    }}
                  /> */}
                  <If condition={hasPermission("delete-report")}>
                    <DeleteBtn
                      params={{
                        func: deleteReportData,
                        funcParams: item?.uuid,
                      }}
                      updateFront={() =>
                        updateFront((prev) => ({
                          ...prev,
                          data: [
                            ...prev?.data?.filter(
                              (data) => data?.uuid !== item?.uuid
                            ),
                          ],
                        }))
                      }
                      referenceLabel={item?.name}
                    />
                  </If>
                </div>
              </TableCell>
            </TableRow>
          ))}
          {sortedData.length < 1 && (
            <TableRow>
              <TableCell colSpan={10}>Nada foi encontrado.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="flex justify-between mb-3">
        {dataApi?.data.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
};
